import axios from "axios"
import { load } from "cheerio"
import { env } from "@/env"
import type { CartShippingAddress } from "@/services/cart-service"
import { toWordPressOrigin } from "@/services/cart-service"

const UPDATE_ORDER_REVIEW_NONCE_REGEX = /"update_order_review_nonce":"([^"]+)"/
const TRIIBO_INSTALLMENTS_NONCE_REGEX =
  /name=["']triibo_installments_security["'][^>]*value=["']([^"']+)["']/
const SHIPPING_METHOD_INPUT_SELECTOR = "input[name='shipping_method[0]']"
const DEFAULT_SHIPPING_METHOD_ID = "triibo-fast-shop-shipping241"

export interface CheckoutShippingMethod {
  id: string
  label: string
  price: number
}

export interface CheckoutPaymentInstallment {
  value: string
  label: string
  installmentCount: number
  installmentValue: number
  total: number
  interestLabel: string
}

export interface CheckoutOrderSummaryResult {
  ok: boolean
  reload?: boolean
  totals?: {
    subtotal: number
    shippingTotal: number
    total: number
    currency: string
  }
  shippingMethods?: CheckoutShippingMethod[]
  paymentInstallments?: CheckoutPaymentInstallment[]
  selectedShippingMethod?: string | null
  upstreamSetCookie: string[]
}

interface CheckoutAddressFields {
  firstName: string
  lastName: string
  country: string
  state: string
  city: string
  postcode: string
  address1: string
  address2?: string
  number?: string
  neighborhood?: string
}

interface CheckoutCardFields {
  holderName: string
  holderCpf: string
  holderPhone: string
  number: string
  expiry: string
  cvc: string
  brand: string
  installment: string
}

interface CheckoutSubmitRequestBase {
  shipToDifferentAddress: boolean
  shipping?: CheckoutAddressFields
  billing: CheckoutAddressFields & {
    email: string
    phone: string
    cpf: string
  }
  orderNotes?: string
  termsAccepted?: boolean
}

type CheckoutSubmitRequestByMethod =
  | {
      paymentMethod: "bacs" | "triibo-payments-pix"
      card?: never
    }
  | {
      paymentMethod: "triibo-payments-card"
      card: CheckoutCardFields
    }

export type CheckoutSubmitRequest = CheckoutSubmitRequestBase &
  CheckoutSubmitRequestByMethod

export interface CheckoutSubmitResult {
  ok: boolean
  redirectUrl?: string
  code?:
    | "CHECKOUT_REJECTED"
    | "CHECKOUT_SUBMIT_FAILED"
    | "CHECKOUT_CONTEXT_UNAVAILABLE"
  message?: string
  upstreamSetCookie: string[]
}

interface CheckoutOrderReviewResponse {
  reload?: boolean
  result?: string
  fragments?: Record<string, string>
}

interface TriiboInstallmentOptionResponse {
  installments?: number
  value?: number
  total?: number
  label?: string
}

interface TriiboInstallmentAjaxResponse {
  success?: boolean
  data?: {
    installment_options?: TriiboInstallmentOptionResponse[]
  }
}

interface WooCheckoutSubmitResponse {
  result?: string
  redirect?: string
  messages?: string
}

const ORDER_RECEIVED_PATH_REGEX = /\/order-received\/(\d+)\/?/

const parseHeaderValues = (value: string | string[] | undefined): string[] => {
  if (Array.isArray(value)) {
    return value
  }

  if (!value) {
    return []
  }

  return [value]
}

const PROCESS_CHECKOUT_NONCE_REGEXES = [
  /name=["']woocommerce-process-checkout-nonce["'][^>]*value=["']([^"']+)["']/,
  /"woocommerce-process-checkout-nonce":"([^"]+)"/,
]
const HTML_LIST_ITEM_REGEX = /<li[^>]*>([\s\S]*?)<\/li>/i
const HTML_TAG_REGEX = /<[^>]+>/g
const HTML_NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: " ",
  quot: '"',
}

const parseBrazilCurrency = (text: string): number => {
  const cleaned = text
    .replace(/\s/g, "")
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".")

  if (!cleaned) {
    return 0
  }

  const parsed = Number.parseFloat(cleaned)

  return Number.isNaN(parsed) ? 0 : parsed
}

const extractProcessCheckoutNonce = (html: string): string | null => {
  const $ = load(html)
  const nonceFromInput =
    $("input[name='woocommerce-process-checkout-nonce']")
      .first()
      .attr("value")
      ?.trim() ?? ""

  if (nonceFromInput) {
    return nonceFromInput
  }

  for (const matcher of PROCESS_CHECKOUT_NONCE_REGEXES) {
    const matched = html.match(matcher)?.[1]?.trim()
    if (matched) {
      return matched
    }
  }

  return null
}

const decodeHtmlEntities = (value: string): string => {
  return value.replace(/&(#x?[\da-f]+|[a-z]+);/gi, (entity, token: string) => {
    if (token.startsWith("#x") || token.startsWith("#X")) {
      const codePoint = Number.parseInt(token.slice(2), 16)

      return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint)
    }

    if (token.startsWith("#")) {
      const codePoint = Number.parseInt(token.slice(1), 10)

      return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint)
    }

    return HTML_NAMED_ENTITIES[token.toLowerCase()] ?? entity
  })
}

const normalizeWooSubmitFailureMessage = (
  rawHtml: string | undefined
): string => {
  const content = rawHtml?.trim()

  if (!content) {
    return "Nao foi possivel finalizar compra."
  }

  const candidate = content.match(HTML_LIST_ITEM_REGEX)?.[1] ?? content

  const normalized = decodeHtmlEntities(candidate)
    .replace(HTML_TAG_REGEX, " ")
    .replace(/\s+/g, " ")
    .trim()

  return normalized || "Nao foi possivel finalizar compra."
}

const toAppThankYouRedirect = (redirectUrl: string): string | null => {
  const trimmedRedirect = redirectUrl.trim()

  if (!trimmedRedirect) {
    return null
  }

  try {
    const parsedRedirectUrl = new URL(trimmedRedirect)
    const orderId = parsedRedirectUrl.pathname.match(
      ORDER_RECEIVED_PATH_REGEX
    )?.[1]

    if (!orderId) {
      return null
    }

    const queryParams = new URLSearchParams({ orderId })
    const orderKey = parsedRedirectUrl.searchParams.get("key")?.trim()

    if (orderKey) {
      queryParams.set("key", orderKey)
    }

    return `/obrigado?${queryParams.toString()}`
  } catch {
    return null
  }
}

const setIfPresent = (
  body: URLSearchParams,
  key: string,
  value: string | undefined
): void => {
  const normalized = value?.trim()

  if (!normalized) {
    return
  }

  body.set(key, normalized)
}

const applyAddressFields = (
  body: URLSearchParams,
  prefix: "billing" | "shipping",
  input: {
    firstName: string
    lastName: string
    country: string
    state: string
    city: string
    postcode: string
    address1: string
    address2?: string
    number?: string
    neighborhood?: string
  }
): void => {
  body.set(`${prefix}_first_name`, input.firstName.trim())
  body.set(`${prefix}_last_name`, input.lastName.trim())
  body.set(`${prefix}_country`, input.country.trim())
  body.set(`${prefix}_state`, input.state.trim())
  body.set(`${prefix}_city`, input.city.trim())
  body.set(`${prefix}_postcode`, input.postcode.trim())
  body.set(`${prefix}_address_1`, input.address1.trim())
  body.set(`${prefix}_address_2`, input.address2?.trim() ?? "")
  setIfPresent(body, `${prefix}_number`, input.number)
  setIfPresent(body, `${prefix}_neighborhood`, input.neighborhood)
}

const extractCheckoutNonce = (html: string): string | null => {
  return html.match(UPDATE_ORDER_REVIEW_NONCE_REGEX)?.[1] ?? null
}

const extractTriiboInstallmentsNonce = (html: string): string | null => {
  return html.match(TRIIBO_INSTALLMENTS_NONCE_REGEX)?.[1] ?? null
}

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  currency: "BRL",
  style: "currency",
})

const formatInstallmentCurrency = (value: number): string =>
  currencyFormatter.format(value).replace(/\u00a0/g, " ")

const normalizePaymentInstallments = (
  options: TriiboInstallmentOptionResponse[] = []
): CheckoutPaymentInstallment[] => {
  const installments: CheckoutPaymentInstallment[] = []

  for (const option of options) {
    const installmentCount = Number(option.installments)
    const installmentValue = Number(option.value)
    const total = Number(option.total)
    const interestLabel = option.label?.trim() || "sem juros"

    if (
      !Number.isFinite(installmentCount) ||
      installmentCount <= 0 ||
      !Number.isFinite(installmentValue) ||
      !Number.isFinite(total)
    ) {
      continue
    }

    installments.push({
      value: String(installmentCount),
      label: `${installmentCount}x de ${formatInstallmentCurrency(installmentValue)} ${interestLabel}`,
      installmentCount,
      installmentValue,
      total,
      interestLabel,
    })
  }

  return installments
}

const resolveShippingAddress = (shippingAddress?: CartShippingAddress) => {
  return {
    country: shippingAddress?.country?.trim() || "BR",
    state: shippingAddress?.state?.trim() || "SP",
    postcode: shippingAddress?.postcode?.trim() || "",
    city: shippingAddress?.city?.trim() || "",
  }
}

const parseOrderReviewFragments = (
  fragments: Record<string, string> | undefined
): Omit<CheckoutOrderSummaryResult, "ok" | "reload" | "upstreamSetCookie"> => {
  const reviewHtml =
    fragments?.[".woocommerce-checkout-review-order-table"] ?? ""
  const $ = load(reviewHtml)

  const shippingMethods = $(SHIPPING_METHOD_INPUT_SELECTOR)
    .toArray()
    .map((element) => {
      const input = $(element)
      const labelElement = input.parent().find("label")
      const labelText = labelElement.text().replace(/\s+/g, " ").trim()
      const amountFromLabel = labelElement.find(".amount").first().text().trim()

      return {
        id: input.attr("value") ?? "",
        label: labelText,
        price: parseBrazilCurrency(amountFromLabel || labelText),
      }
    })
    .filter((method) => method.id.length > 0)

  return {
    totals: {
      subtotal: parseBrazilCurrency(
        $(".cart-subtotal .amount").first().text().trim()
      ),
      shippingTotal: parseBrazilCurrency(
        $(".shipping .amount").first().text().trim()
      ),
      total: parseBrazilCurrency(
        $(".order-total .amount").first().text().trim()
      ),
      currency: "BRL",
    },
    shippingMethods,
    selectedShippingMethod:
      $(`${SHIPPING_METHOD_INPUT_SELECTOR}:checked`).attr("value") ?? null,
  }
}

export const fetchCheckoutOrderSummary = async (
  sessionToken: string,
  shippingAddress?: CartShippingAddress
): Promise<CheckoutOrderSummaryResult> => {
  const wordPressOrigin = toWordPressOrigin(env.WP_API_URL)
  const checkoutPageUrl = `${wordPressOrigin}/finalizacao-de-compra/`
  const requestHeaders = sessionToken.trim() ? { Cookie: sessionToken } : {}
  const checkoutPageResponse = await axios.get<string>(checkoutPageUrl, {
    headers: requestHeaders,
  })

  const upstreamSetCookie = parseHeaderValues(
    (
      checkoutPageResponse.headers as Record<
        string,
        string | string[] | undefined
      >
    )["set-cookie"]
  )
  const checkoutPageHtml =
    typeof checkoutPageResponse.data === "string"
      ? checkoutPageResponse.data
      : ""
  const checkoutNonce = extractCheckoutNonce(checkoutPageHtml)
  const triiboInstallmentsNonce =
    extractTriiboInstallmentsNonce(checkoutPageHtml)

  if (!checkoutNonce) {
    throw new Error("CHECKOUT_NONCE_NOT_FOUND")
  }

  const address = resolveShippingAddress(shippingAddress)
  const payload = new URLSearchParams()

  payload.set("security", checkoutNonce)
  payload.set("payment_method", "triibo-payments-card")
  payload.set("country", address.country)
  payload.set("state", address.state)
  payload.set("postcode", address.postcode)
  payload.set("city", address.city)
  payload.set("address", "")
  payload.set("address_2", "")
  payload.set("s_country", address.country)
  payload.set("s_state", address.state)
  payload.set("s_postcode", address.postcode)
  payload.set("s_city", address.city)
  payload.set("s_address", "")
  payload.set("s_address_2", "")
  payload.set("has_full_address", "false")
  payload.set(
    "post_data",
    `_wcf_checkout_id=${env.WCF_CHECKOUT_ID}&payment_method=triibo-payments-card`
  )
  payload.set("shipping_method[0]", DEFAULT_SHIPPING_METHOD_ID)

  const updateOrderReviewUrl = `${wordPressOrigin}/finalizacao-de-compra/?wc-ajax=update_order_review&wcf_checkout_id=${env.WCF_CHECKOUT_ID}`
  const updateOrderReviewResponse = await axios.post<
    CheckoutOrderReviewResponse | string
  >(updateOrderReviewUrl, payload.toString(), {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      ...requestHeaders,
    },
  })

  const secondSetCookie = parseHeaderValues(
    (
      updateOrderReviewResponse.headers as Record<
        string,
        string | string[] | undefined
      >
    )["set-cookie"]
  )
  let mergedSetCookie = [...upstreamSetCookie, ...secondSetCookie]

  if (
    typeof updateOrderReviewResponse.data === "string" &&
    updateOrderReviewResponse.data.trim() === "-1"
  ) {
    throw new Error("ORDER_REVIEW_REJECTED")
  }

  const payloadData =
    typeof updateOrderReviewResponse.data === "string"
      ? ({} as CheckoutOrderReviewResponse)
      : updateOrderReviewResponse.data

  if (payloadData.reload === true) {
    return {
      ok: false,
      reload: true,
      upstreamSetCookie: mergedSetCookie,
    }
  }

  let paymentInstallments: CheckoutPaymentInstallment[] = []

  if (triiboInstallmentsNonce) {
    const installmentPayload = new URLSearchParams()
    installmentPayload.set("action", "triibo_get_updated_installment_data")
    installmentPayload.set(
      "triibo_installments_security",
      triiboInstallmentsNonce
    )

    const installmentResponse = await axios.post<TriiboInstallmentAjaxResponse>(
      `${wordPressOrigin}/wp-admin/admin-ajax.php`,
      installmentPayload.toString(),
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          ...requestHeaders,
        },
      }
    )

    const installmentSetCookie = parseHeaderValues(
      (
        installmentResponse.headers as Record<
          string,
          string | string[] | undefined
        >
      )["set-cookie"]
    )
    mergedSetCookie = [...mergedSetCookie, ...installmentSetCookie]

    if (installmentResponse.data.success) {
      paymentInstallments = normalizePaymentInstallments(
        installmentResponse.data.data?.installment_options
      )
    }
  }

  return {
    ok: true,
    ...parseOrderReviewFragments(payloadData.fragments),
    paymentInstallments,
    upstreamSetCookie: mergedSetCookie,
  }
}

export const submitCheckoutOrder = async (
  sessionToken: string,
  payload: CheckoutSubmitRequest
): Promise<CheckoutSubmitResult> => {
  if (payload.shipToDifferentAddress && !payload.shipping) {
    throw new Error("CHECKOUT_SUBMIT_FAILED")
  }

  const wordPressOrigin = toWordPressOrigin(env.WP_API_URL)
  const requestHeaders = sessionToken.trim() ? { Cookie: sessionToken } : {}

  const checkoutPageResponse = await axios.get<string>(
    `${wordPressOrigin}/finalizacao-de-compra/`,
    {
      headers: requestHeaders,
    }
  )

  const checkoutPageCookies = parseHeaderValues(
    (
      checkoutPageResponse.headers as Record<
        string,
        string | string[] | undefined
      >
    )["set-cookie"]
  )
  const checkoutPageHtml =
    typeof checkoutPageResponse.data === "string"
      ? checkoutPageResponse.data
      : ""
  const processCheckoutNonce = extractProcessCheckoutNonce(checkoutPageHtml)

  if (!processCheckoutNonce) {
    throw new Error("CHECKOUT_CONTEXT_UNAVAILABLE")
  }

  const body = new URLSearchParams()
  body.set("payment_method", payload.paymentMethod)
  body.set("_wcf_checkout_id", env.WCF_CHECKOUT_ID)
  body.set("woocommerce-process-checkout-nonce", processCheckoutNonce)
  body.set("_wp_http_referer", "/finalizacao-de-compra/")
  body.set(
    "ship_to_different_address",
    payload.shipToDifferentAddress ? "1" : "0"
  )
  body.set("woocommerce_checkout_place_order", "1")

  applyAddressFields(body, "billing", payload.billing)
  body.set("billing_email", payload.billing.email.trim())
  body.set("billing_phone", payload.billing.phone.trim())
  setIfPresent(body, "billing_cpf", payload.billing.cpf)

  if (payload.paymentMethod === "triibo-payments-card" && payload.card) {
    setIfPresent(
      body,
      "triibo-payments-card-card-holder-name",
      payload.card.holderName
    )
    setIfPresent(
      body,
      "triibo-payments-card-card-holder-cpf",
      payload.card.holderCpf
    )
    setIfPresent(
      body,
      "triibo-payments-card-card-holder-phone",
      payload.card.holderPhone
    )
    setIfPresent(body, "triibo-payments-card-card-number", payload.card.number)
    setIfPresent(body, "triibo-payments-card-card-expiry", payload.card.expiry)
    setIfPresent(body, "triibo-payments-card-card-cvc", payload.card.cvc)
    setIfPresent(body, "triibo-payments-card-card-brand", payload.card.brand)
    setIfPresent(
      body,
      "triibo-payments-card-card-installment",
      payload.card.installment
    )
  }

  if (payload.shipToDifferentAddress) {
    applyAddressFields(
      body,
      "shipping",
      payload.shipping as CheckoutAddressFields
    )
  }

  if (payload.orderNotes?.trim()) {
    body.set("order_comments", payload.orderNotes.trim())
  }

  if (payload.termsAccepted) {
    body.set("terms", "on")
  }

  const checkoutSubmitResponse = await axios.post<
    WooCheckoutSubmitResponse | string
  >(
    `${wordPressOrigin}/?wc-ajax=checkout&wcf_checkout_id=${env.WCF_CHECKOUT_ID}`,
    body.toString(),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        ...requestHeaders,
      },
    }
  )

  const checkoutSubmitCookies = parseHeaderValues(
    (
      checkoutSubmitResponse.headers as Record<
        string,
        string | string[] | undefined
      >
    )["set-cookie"]
  )
  const upstreamSetCookie = [...checkoutPageCookies, ...checkoutSubmitCookies]
  const responseData =
    typeof checkoutSubmitResponse.data === "string"
      ? ({} as WooCheckoutSubmitResponse)
      : checkoutSubmitResponse.data

  if (
    responseData.result === "success" &&
    typeof responseData.redirect === "string" &&
    responseData.redirect.trim()
  ) {
    if (payload.paymentMethod === "triibo-payments-pix") {
      return {
        ok: true,
        redirectUrl: responseData.redirect,
        upstreamSetCookie,
      }
    }

    const appRedirectUrl = toAppThankYouRedirect(responseData.redirect)

    if (!appRedirectUrl) {
      throw new Error("CHECKOUT_SUBMIT_FAILED")
    }

    return {
      ok: true,
      redirectUrl: appRedirectUrl,
      upstreamSetCookie,
    }
  }

  if (responseData.result === "failure") {
    return {
      ok: false,
      code: "CHECKOUT_REJECTED",
      message: normalizeWooSubmitFailureMessage(responseData.messages),
      upstreamSetCookie,
    }
  }

  throw new Error("CHECKOUT_SUBMIT_FAILED")
}
